import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Privacy Policy | Ludo Room';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <main className="privacy-page">
      <article className="privacy-card">
        <header className="privacy-header">
          <Link className="privacy-home-link" to="/">← Back to Ludo Room</Link>
          <p className="privacy-eyebrow">Ludo Room</p>
          <h1>Privacy Policy</h1>
          <p className="privacy-updated">Effective and last updated: August 1, 2026</p>
        </header>

        <section className="privacy-identity" aria-label="Application identity">
          <div><span>App name</span><strong>Ludo Room</strong></div>
          <div><span>Android package</span><strong>com.ludoroom.king</strong></div>
          <div><span>Owner / developer</span><strong>Rajni Web Developer</strong></div>
        </section>

        <section>
          <h2>1. About this policy</h2>
          <p>
            This Privacy Policy explains how Ludo Room (the “App”, “we”, “us”, or
            “our”) collects, uses, shares, and protects information when you use the
            Ludo Room Android application, website, multiplayer game, and related
            services. By using Ludo Room, you acknowledge the practices described here.
          </p>
        </section>

        <section>
          <h2>2. Information we collect</h2>
          <ul>
            <li><strong>Profile and account information:</strong> player name and, when you choose to provide them, mobile number and email address.</li>
            <li><strong>Game information:</strong> room identifiers, game settings, moves, results, scores, rewards, and gameplay history.</li>
            <li><strong>Technical information:</strong> IP address, browser or device type, operating system, app version, diagnostics, timestamps, and network information.</li>
            <li><strong>Session information:</strong> login or session identifiers needed to authenticate you, reconnect games, prevent abuse, and keep multiplayer rooms working.</li>
            <li><strong>Communications:</strong> messages or support information you send to us and content you submit through game features.</li>
            <li><strong>Advertising and notification data:</strong> advertising identifiers, ad interaction data, and push-notification tokens where these features are enabled.</li>
          </ul>
          <p>We do not intentionally collect precise location, contacts, photos, or files from your device.</p>
        </section>

        <section>
          <h2>3. How we use information</h2>
          <p>We use information to:</p>
          <ul>
            <li>provide accounts, game rooms, multiplayer connections, results, rewards, and customer support;</li>
            <li>restore sessions and synchronize gameplay across connected players;</li>
            <li>operate, secure, troubleshoot, analyze, and improve Ludo Room;</li>
            <li>detect cheating, spam, fraud, abuse, and violations of our rules;</li>
            <li>send service messages and, with permission, push notifications;</li>
            <li>display, measure, and improve advertising where ads are available; and</li>
            <li>comply with legal obligations and enforce our rights.</li>
          </ul>
        </section>

        <section>
          <h2>4. Permissions</h2>
          <p>
            Ludo Room uses internet and network-state access to connect to multiplayer
            services, load game content, show ads where enabled, and deliver online
            features. Notification permission may be requested on supported devices.
            You can manage optional permissions in your device settings.
          </p>
        </section>

        <section>
          <h2>5. Service providers and sharing</h2>
          <p>
            We may share only the information needed with hosting, database, analytics,
            advertising, notification, security, and support providers that process data
            for us. For example, the App may use Google Mobile Ads and a push-notification
            provider. Those providers may process device identifiers, IP addresses, and
            interaction data under their own privacy terms.
          </p>
          <p>
            We may also disclose information when required by law, to protect users or
            our services, during a business transfer, or when you direct or consent to
            the disclosure. We do not sell your personal information for money.
          </p>
        </section>

        <section>
          <h2>6. Data retention and security</h2>
          <p>
            We retain information for as long as reasonably necessary to provide the
            service, maintain game and transaction records, resolve disputes, prevent
            abuse, and meet legal requirements. Retention periods vary by data type.
            We use reasonable administrative and technical safeguards, but no online
            service can guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>7. Your choices and rights</h2>
          <p>
            You may decline optional information, disable notifications, reset or limit
            your advertising identifier through device settings, and contact us to ask
            for access, correction, or deletion of your personal information. We may
            need to verify your request and may retain information where legally required
            or necessary for security and fraud prevention.
          </p>
        </section>

        <section>
          <h2>8. Children’s privacy</h2>
          <p>
            Ludo Room is not intended for children under 13, or the minimum digital age
            required in their country, without authorization from a parent or guardian.
            We do not knowingly collect personal information from children in violation
            of applicable law. A parent or guardian who believes a child provided such
            information should contact us so we can review and delete it where required.
          </p>
        </section>

        <section>
          <h2>9. International processing</h2>
          <p>
            Our service providers and servers may process information in countries other
            than yours. Where required, we take reasonable steps to protect information
            in accordance with applicable law.
          </p>
        </section>

        <section>
          <h2>10. Changes to this policy</h2>
          <p>
            We may update this policy as Ludo Room or legal requirements change. The
            revised policy will be posted at this URL with a new effective date. Continued
            use after an update means the revised policy applies to your use of the App.
          </p>
        </section>

        <section className="privacy-contact">
          <h2>11. Contact us</h2>
          <p>For privacy questions or data requests, contact the app owner:</p>
          <p>
            <strong>Rajni Web Developer</strong><br />
            Owner and developer of Ludo Room<br />
            Contact person: Rajani Kant Mahato<br />
            Package: <code>com.ludoroom.king</code><br />
            Email: <a href="mailto:support@rajniwebdeveloper.com">support@rajniwebdeveloper.com</a>
          </p>
        </section>
      </article>
    </main>
  );
};

export default PrivacyPolicy;
